import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { authApi } from "../apis/authApi";
import { usePsaStatus } from "../hooks/usePsaStatus";
import { ConnectWisePsaGuideInner } from "./integrationGuides/ConnectWisePsaGuideInner";

// Component: ConnectWiseSetupForm
// Design elements: PSA setup form, credentials inputs, test/save actions.

interface ConnectWiseSetupFormProps {
  onBack: () => void;
  onSuccess?: () => void;
  onCredentialsSaved?: (platformName: string) => void;
}

const ConnectWiseSetupForm: React.FC<ConnectWiseSetupFormProps> = ({ onBack, onSuccess, onCredentialsSaved }) => {
  const { refetch: refetchPsaStatus } = usePsaStatus();
  const [baseUrl, setBaseUrl] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [publicKey, setPublicKey] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [isTesting, setIsTesting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  const testedValuesRef = useRef<{
    baseUrl: string;
    companyId: string;
    publicKey: string;
    privateKey: string;
  } | null>(null);

  const hasFieldsChanged = () => {
    if (!testedValuesRef.current || !testResult?.success) {
      return true;
    }
    return (
      baseUrl !== testedValuesRef.current.baseUrl ||
      companyId !== testedValuesRef.current.companyId ||
      publicKey !== testedValuesRef.current.publicKey ||
      privateKey !== testedValuesRef.current.privateKey
    );
  };

  const isSaveDisabled = !testResult?.success || hasFieldsChanged() || isSaving;

  const handleTestConnection = async () => {
    if (!baseUrl || !companyId || !publicKey || !privateKey) {
      setTestResult({
        success: false,
        message: "Please fill in all fields before testing the connection.",
      });
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const result = await authApi.testConnectWiseConnection({
        base_url: baseUrl,
        company_id: companyId,
        public_key: publicKey,
        private_key: privateKey,
      });

      if (result.success) {
        testedValuesRef.current = {
          baseUrl,
          companyId,
          publicKey,
          privateKey,
        };
      }

      setTestResult({
        success: result.success,
        message: result.message || "Connection test successful!",
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Connection test failed. Please check your credentials.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
      testedValuesRef.current = null;
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async () => {
    if (isSaveDisabled) {
      return;
    }

    setIsSaving(true);

    try {
      await authApi.saveConnectWiseConfiguration({
        base_url: baseUrl,
        company_id: companyId,
        public_key: publicKey,
        private_key: privateKey,
      });

      setShowSuccessSnackbar(true);
      await refetchPsaStatus();
      
      if (onCredentialsSaved) {
        setTimeout(() => onCredentialsSaved("connectwise"), 800);
        return;
      }
      if (onSuccess) {
        setTimeout(() => onSuccess(), 1500);
        return;
      }
      setTimeout(() => {
        window.location.href = "/";
      }, 1500);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to save ConnectWise configuration.";
      setTestResult({
        success: false,
        message: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={onBack}
        sx={{
          fontFamily: "var(--theo-font-sans)",
          color: "#6c6c6c",
          textTransform: "none",
          mb: 3,
          "&:hover": {
            background: "#f5f5f5",
          },
        }}
      >
        Back to PSA Selection
      </Button>

      <Typography
        variant="h5"
        component="h2"
        sx={{
          fontFamily: "var(--theo-font-sans)",
          fontWeight: 700,
          mb: 3,
          color: "#1a1a1a",
        }}
      >
        ConnectWise Setup
      </Typography>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          background: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #e5e5e5",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            mb: 2,
            color: "#1a1a1a",
          }}
        >
          How to connect ConnectWise
        </Typography>
        <Box sx={{ color: "#6c6c6c", "& .MuiTypography-root": { color: "inherit" } }}>
          <ConnectWisePsaGuideInner showGuideTitle={false} />
        </Box>
      </Paper>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <TextField
          label="Base URL"
          value={baseUrl}
          onChange={(e) => {
            setBaseUrl(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          placeholder="https://your-connectwise-host"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="ConnectWise instance root URL."
        />

        <TextField
          label="Company ID"
          value={companyId}
          onChange={(e) => {
            setCompanyId(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Same Company ID used on the ConnectWise login screen."
        />

        <TextField
          label="Public Key"
          value={publicKey}
          onChange={(e) => {
            setPublicKey(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Generated from the API member's API Keys tab in ConnectWise PSA."
        />

        <TextField
          label="Private Key"
          value={privateKey}
          onChange={(e) => {
            setPrivateKey(e.target.value);
            if (testResult?.success) {
              setTestResult(null);
            }
          }}
          type="password"
          fullWidth
          required
          sx={{
            fontFamily: "var(--theo-font-sans)",
            "& .MuiInputLabel-root": {
              fontFamily: "var(--theo-font-sans)",
            },
            "& .MuiInputBase-input": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
          helperText="Only shown once when the API key pair is created. Create a new pair if you lost it."
        />
      </Box>

      {testResult && (
        <Alert
          severity={testResult.success ? "success" : "error"}
          sx={{
            mt: 3,
            fontFamily: "var(--theo-font-sans)",
            "& .MuiAlert-message": {
              fontFamily: "var(--theo-font-sans)",
            },
          }}
        >
          {testResult.message}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handleTestConnection}
          disabled={isTesting}
          sx={{
            fontFamily: "var(--theo-font-sans)",
            textTransform: "none",
            borderColor: "#705d4a",
            color: "#705d4a",
            "&:hover": {
              borderColor: "#604d3a",
              background: "#f5f5f5",
            },
          }}
        >
          {isTesting ? <CircularProgress size={20} /> : "Test Connection"}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled}
          sx={{
            fontFamily: "var(--theo-font-sans)",
            textTransform: "none",
            background: isSaveDisabled ? "#e0e0e0" : "#705d4a",
            color: isSaveDisabled ? "#9e9e9e" : "#fff",
            "&:hover": {
              background: isSaveDisabled ? "#e0e0e0" : "#604d3a",
            },
          }}
        >
          {isSaving ? <CircularProgress size={20} /> : "Save Configuration"}
        </Button>
      </Box>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={2000}
        onClose={() => setShowSuccessSnackbar(false)}
        message="ConnectWise configuration saved successfully!"
      />
    </Box>
  );
};

export default ConnectWiseSetupForm;
